import React, { useCallback, useContext, useState } from 'react'
import {
  Button,
  ButtonGroup,
  Editable,
  EditableInput,
  Flex,
  IconButton,
  Input,
  useEditableControls,
} from '@chakra-ui/react'
import { AddIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons'
import { CreateNodeParams, TreeViewContext } from './TreeViewContext'
import { IReportComponentBlueprint } from '../../../../services/decoders/blueprintDecoder'

export interface IButtonAddNodeProps {
  target: IReportComponentBlueprint[]
  label: string | React.ReactNode
  type: string
  parentId?: string
  onSubmit?: (params: CreateNodeParams) => void
}
export const ButtonCreateNode = ({
  target,
  label,
  type,
  parentId,
  onSubmit,
}: IButtonAddNodeProps) => {
  const { readOnly } = useContext(TreeViewContext)

  const [value, setValue] = useState('')
  const handleChange = text => {
    setValue(text)
  }

  const handleSubmit = useCallback(() => {
    if (!value || value.length <= 1) return

    if (onSubmit) onSubmit({ parent: target, nodeTitle: value, parentId, type })
  }, [onSubmit, target, value, parentId, type])

  if (readOnly) return null
  return (
    <Editable
      onBlur={f => f}
      onChange={handleChange}
      onSubmit={handleSubmit}
      position="relative">
      <Flex
        mb="12px"
        alignItems="center"
        _before={{
          content: '""',
          position: 'absolute',
          top: '8px',
          left: '-40px',
          width: '50px',
          height: '12px',
          border: '1px solid',
          borderColor: 'brand.gray',
          borderTop: 'none',
          borderRight: 'none',
        }}>
        <Input
          as={EditableInput}
          p="4px 0px 4px 12px"
          fontSize="14px"
          w="100%"
          h="32px"
          mr="4px"
          _focus={{
            outline: 'none',
            boxShadow: 'none',
            outlineOffset: '0',
            border: '1px solid',
            borderColor: 'brand.orange',
          }}
          placeholder={`Add ${label}`}
        />

        <EditableControls>{label}</EditableControls>
      </Flex>
    </Editable>
  )
}

export interface EditableControlsProps {
  children: React.ReactNode
}
export const EditableControls = ({ children }: EditableControlsProps) => {
  const {
    isEditing,
    getSubmitButtonProps,
    getCancelButtonProps,
    getEditButtonProps,
  } = useEditableControls()

  return isEditing ? (
    <ButtonGroup
      size="sm"
      spacing={1}>
      <IconButton
        variant="transparent"
        _hover={{ bg: 'brand.lightestGray' }}
        aria-label="Save"
        icon={
          <CheckIcon
            fontSize="12px"
            color="brand.green"
          />
        }
        {...getSubmitButtonProps()}
      />
      <IconButton
        variant="transparent"
        _hover={{ bg: 'brand.lightestGray' }}
        aria-label="Cancel"
        icon={
          <CloseIcon
            fontSize="11px"
            color="brand.red"
          />
        }
        {...getCancelButtonProps()}
      />
    </ButtonGroup>
  ) : (
    <Button
      mt="8px"
      size="xs"
      color="brand.textDarkBlue"
      fontSize="14px"
      rightIcon={<AddIcon fontSize="8px" />}
      {...getEditButtonProps()}
      _hover={{
        bg: 'brand.lightGray',
      }}>
      {children}
    </Button>
  )
}
